// MobileMonthView.tsx — kompaktes Monatsraster für die Mobile-Ansicht
// (`/termine`, SP6). Rasterlogik 1:1 aus MiniMonth.tsx (42-Zellen-isoWeek-Grid,
// `cursor`-State zum Blättern, Event-Punkte), aber vollbreit und in
// REFINED-Farben (statt der dort hartkodierten AUB/GOLD). Tag-Tap → onPickDay
// (Shell setzt currentDate + view='day'). Eigener `cursor`, damit Blättern die
// Auswahl nicht ändert; folgt `value` bei externem Wechsel.

import React, { useMemo, useState } from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)
import { REFINED } from "../risikoanalyseStep/tokens";

export interface MobileMonthViewProps {
    value: Date;
    /** setzt currentDate + view='day' (Shell). */
    onPickDay: (d: Date) => void;
    events?: { start: Date }[];
}

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export const MobileMonthView: React.FC<MobileMonthViewProps> = ({ value, onPickDay, events }) => {
    const [cursor, setCursor] = useState<moment.Moment>(moment(value).startOf("month"));

    // Externer value-Wechsel (z. B. via DaySelector/Heute) zieht den Monat mit.
    React.useEffect(() => {
        setCursor(moment(value).startOf("month"));
    }, [value]);

    const eventDayKeys = useMemo(() => {
        const set = new Set<string>();
        (events || []).forEach((e) => {
            if (e?.start) set.add(moment(e.start).format("YYYY-MM-DD"));
        });
        return set;
    }, [events]);

    const cells = useMemo(() => {
        const gridStart = cursor.clone().startOf("month").startOf("isoWeek");
        const days: moment.Moment[] = [];
        for (let i = 0; i < 42; i++) days.push(gridStart.clone().add(i, "days"));
        return days;
    }, [cursor]);

    const todayKey = moment().format("YYYY-MM-DD");
    const valueKey = moment(value).format("YYYY-MM-DD");
    const monthKey = cursor.format("YYYY-MM");

    return (
        <div style={wrapStyle}>
            {/* Kopf: Monat/Jahr + Blätterpfeile (≥44px Tap-Ziele). */}
            <div style={headStyle}>
                <button
                    type="button"
                    aria-label="Vorheriger Monat"
                    onClick={() => setCursor((c) => c.clone().subtract(1, "month"))}
                    style={navBtnStyle}
                >
                    ‹
                </button>
                <span style={monthLabelStyle}>{cursor.format("MMMM YYYY")}</span>
                <button
                    type="button"
                    aria-label="Nächster Monat"
                    onClick={() => setCursor((c) => c.clone().add(1, "month"))}
                    style={navBtnStyle}
                >
                    ›
                </button>
            </div>

            {/* Wochentags-Kopf */}
            <div style={weekHeadStyle}>
                {WEEKDAYS.map((d) => (
                    <div key={d} style={{ padding: "4px 0" }}>
                        {d}
                    </div>
                ))}
            </div>

            {/* Tage-Raster (füllt die verbleibende Höhe). */}
            <div style={gridStyle}>
                {cells.map((day) => {
                    const key = day.format("YYYY-MM-DD");
                    const inMonth = day.format("YYYY-MM") === monthKey;
                    const isToday = key === todayKey;
                    const isSelected = key === valueKey;
                    const hasEvent = eventDayKeys.has(key);
                    const isWeekend = day.isoWeekday() >= 6;
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onPickDay(day.toDate())}
                            title={day.format("dddd, D. MMMM YYYY")}
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "none",
                                borderRadius: 8,
                                cursor: "pointer",
                                font: `${isSelected || isToday ? 700 : 500} 14px ${REFINED.fontUI}`,
                                minHeight: 44,
                                background: isSelected
                                    ? REFINED.aub
                                    : isToday
                                      ? REFINED.aub100
                                      : "transparent",
                                color: isSelected
                                    ? REFINED.white
                                    : inMonth
                                      ? isWeekend
                                          ? REFINED.ink3
                                          : REFINED.ink
                                      : REFINED.ink3,
                                opacity: inMonth ? 1 : 0.5,
                            }}
                        >
                            {day.date()}
                            {hasEvent && (
                                <span
                                    aria-hidden
                                    style={{
                                        position: "absolute",
                                        bottom: 6,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        background: isSelected ? REFINED.white : REFINED.mag,
                                    }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const wrapStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    userSelect: "none",
};

const headStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
};

const monthLabelStyle: React.CSSProperties = {
    font: `700 16px ${REFINED.fontUI}`,
    color: REFINED.ink,
    textTransform: "capitalize",
};

const navBtnStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    background: REFINED.white,
    color: REFINED.ink2,
    cursor: "pointer",
    font: `600 18px ${REFINED.fontUI}`,
    padding: 0,
    flexShrink: 0,
};

const weekHeadStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    color: REFINED.ink3,
    font: `600 11px ${REFINED.fontUI}`,
    marginBottom: 4,
};

const gridStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gridAutoRows: "1fr",
    gap: 2,
};

export default MobileMonthView;
